import {GlobalEventsMap} from '@gongxigames/framework/slot/events';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {ModuleInitPhase} from '@gongxigames/framework/core';
import {SystemPopup, SystemPopupPopupID} from '@gongxigames/framework/slot/types';
import {SystemPopupsView} from './SystemPopupsView';
import {SystemPopupsModuleBasicState} from './SystemPopupsModuleBasicState';
import {SystemPopupsModuleShowingState} from './SystemPopupsModuleShowingState';
import {PopupsQueue} from './PopupsQueue';
import {SignalEventEmitterWrapper} from '@gongxigames/framework/core/services/strictEventEmitter';
import {strictGameEmitter} from '@gongxigames/framework/slotV2/globals';

export type SystemPopupsManagerEvents = GlobalEventsMap & {
    'SystemPopupsManager.POPUP_SHOWN': any;
    'SystemPopupsManager.POPUP_HID': any;
    'SystemPopupsManager.POPUP_CLOSED': any;
};

export class SystemPopupsManager extends Module<SystemPopupsView, SystemPopupsManagerEvents> {
    public static NAME = Symbol('SystemPopupsManager');
    public static VIEW = SystemPopupsView.NAME;
    public static initPhase: ModuleInitPhase = 'initial';
    public static SHOWING_STATE = 'SHOWING_STATE';
    public static BASIC_STATE = 'BASIC_STATE';

    protected popups: PopupsQueue = new PopupsQueue();
    protected visiblePopup: SystemPopup | undefined;

    protected abortController = new AbortController();
    protected abortSignal = this.abortController.signal;
    readonly uiEmitter = new SignalEventEmitterWrapper(strictGameEmitter, {signal: this.abortSignal, owner: this});

    protected setupViews(): void {
        this.setView(SystemPopupsManager.VIEW);
    }

    public hasPopups(): boolean {
        return !this.popups.isEmpty();
    }

    public showNextPopup(): void {
        const popup = this.popups.shiftNext();

        if (popup) {
            this.showPopup(popup);
        }
    }

    public swapVisiblePopupToNextPopup(): void {
        if (this.visiblePopup) {
            this.popups.add(this.visiblePopup);
            this.view.hideCurrentPopup();
            this.showNextPopup();
        }
    }

    public hideNotPrioritizedPopup(popup: SystemPopup): void {
        if (this.visiblePopup) {
            this.popups.add(this.visiblePopup);
            this.view.hidePopup(popup);
        }
    }

    public isHigherPriorityVisiblePopup(popup: SystemPopup): boolean {
        if (!this.visiblePopup) {
            return false;
        }

        const visibleVisiblePopupPriority = this.visiblePopup.priority ?? 0;
        const popupPriority = popup.priority ?? 0;

        return visibleVisiblePopupPriority > popupPriority;
    }

    public enableUI(): void {
        this.eventManager.emit('General.UI.ENABLE', SystemPopupsManager.NAME); // TODO IVAN
        this.eventManager.emit('General.UI.ENABLE_KEYBOARD', SystemPopupsManager.NAME);
    }

    public disableUI(): void {
        this.eventManager.emit('General.UI.DISABLE', SystemPopupsManager.NAME); // TODO IVAN
        this.eventManager.emit('General.UI.DISABLE_KEYBOARD', SystemPopupsManager.NAME);
    }

    public allPopupsHidden(): void {
        this.eventManager.emit('General.Popups.AllPopupsHidden');
        this.gotoState(SystemPopupsManager.BASIC_STATE);
    }

    protected showPopup(popup: SystemPopup): void {
        this.visiblePopup = popup;
        this.view.showPopup(popup);
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.on('General.Popups.Add', this.onAddPopup);
        this.on('General.Popups.Remove', this.onRemovePopup);
        this.on('General.Popups.RemoveAll', this.removePopups);

        this.onCurrentStateHandle('General.Popups.Add');
        this.onCurrentStateHandle('General.Popups.Remove');
        this.onCurrentStateHandle('General.Popups.RemoveAll');
        this.onCurrentStateHandle('General.Popups.AllPopupsHidden');

        this.onViewCurrentStateHandle('SystemPopupsManager.POPUP_HID');
        this.onViewCurrentStateHandle('SystemPopupsManager.POPUP_SHOWN');
        this.onViewCurrentStateHandle('SystemPopupsManager.POPUP_CLOSED');
    }

    protected setupStates(): void {
        this.addState(new SystemPopupsModuleBasicState(this, SystemPopupsManager.BASIC_STATE));
        this.addState(new SystemPopupsModuleShowingState(this, SystemPopupsManager.SHOWING_STATE));

        this.gotoState(SystemPopupsManager.BASIC_STATE);
    }

    protected onAddPopup(id: string, popup: SystemPopup): void {
        this.popups.add(popup);
    }

    protected onRemovePopup(id: string, popup: SystemPopup | SystemPopupPopupID): void {
        if (typeof popup === 'string') {
            const popupToHide = this.popups.shiftByID(popup);

            if (popupToHide) {
                this.hideIfIsVisiblePopup(popupToHide);
            } else if (this.visiblePopup && this.visiblePopup.id === popup) {
                this.hideIfIsVisiblePopup(this.visiblePopup);
            }
        } else {
            this.popups.remove(popup);
            this.hideIfIsVisiblePopup(popup);
        }
    }

    protected removePopups(): void {
        this.popups.clear();
        this.visiblePopup = undefined;
        this.view.hideCurrentPopup();
    }

    protected hideIfIsVisiblePopup(popup: SystemPopup): void {
        if (this.visiblePopup && popup.id === this.visiblePopup.id) {
            this.visiblePopup = undefined;
            this.view.hidePopup(popup);
        }
    }
}
